import { Theme } from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import { ILocationZone, LocationState, ZonalAztecLocationSettings } from './models/LocationModel';
import { isArray, isArrayOf, isNumber, isObject, isOptional } from 'lib/typeguards';

export function parseTimeRange(value: string) {
    const reg = new RegExp(/([0-9]{1,2}:[0-9]{2})([ap]m)? - ([0-9]{1,2}:[0-9]{2})([ap]m)?/);
    const result = reg.exec(value);
    if (!result) {
        return { fullMatch: null, start: null, end: null };
    }
    const [fullMatch, startValue, startMopdifier, endValue, endModifier] = reg.exec(value);
    return { fullMatch, start: `${startValue}${startMopdifier}`, end: `${endValue}${endModifier}` };
}

export function mapLocationStateToStyles(
    state: LocationState,
    theme: Theme
): { backgroundColor: string; color: string } {
    switch (state) {
        case LocationState.ACTIVE:
            return { backgroundColor: green['A700'], color: theme.palette.primary.contrastText };
        case LocationState.PENDING:
            return { backgroundColor: orange['500'], color: theme.palette.primary.contrastText };
        case LocationState.RETIRED:
        default:
            return { backgroundColor: red['500'], color: theme.palette.primary.contrastText };
    }
}

const isZoneArrayRange = (zoneTableNumbers: number[]) => zoneTableNumbers[0] !== zoneTableNumbers[1];

function isZonalAztecSettings(obj: unknown): obj is ZonalAztecLocationSettings {
    return (
        isObject(obj) &&
        isOptional(isNumber, obj.salesAreaIdItems) &&
        isOptional(isNumber, obj.salesAreaIdDiscounts) &&
        isOptional(isNumber, obj.salesAreaIdPayments)
    );
}

export function isExpandedLocationZone(zone: unknown): zone is {
    tables: [number, number][];
    zonalAztec?: ZonalAztecLocationSettings;
} {
    return (
        isObject(zone) &&
        Array.isArray(zone.tables) &&
        zone.tables.every(e => isArrayOf(isNumber, e) && e.length === 2) &&
        isOptional(isZonalAztecSettings, zone.zonalAztec)
    );
}

export const getZonesString = (zones: ILocationZone, withSpaces = true): string => {
    const isExpanded = !isArray(zones);
    if (isExpanded && 'tables' in zones) {
        return getZonesString(zones.tables, withSpaces);
    }
    const zone = zones;
    return zone
        .map(zone => {
            if (isZoneArrayRange(zone)) {
                return `${zone[0]}-${zone[1]}`;
            }
            return String(zone[0]);
        })
        .join(withSpaces ? ', ' : ',');
};

export const getZonesArrayFromString = (zonesStr: string) => {
    const rangesSeparatedByComma = zonesStr.split(',');

    const resultZones: [number, number][] = [];

    rangesSeparatedByComma.forEach(zone => {
        const rangesFromZoneString = zone.split('-');

        if (rangesFromZoneString.length > 1) {
            resultZones.push([+rangesFromZoneString[0], +rangesFromZoneString[1]]);
        } else {
            resultZones.push([+rangesFromZoneString[0], +rangesFromZoneString[0]]);
        }
    });

    return resultZones;
};
